import $api from "@/zapi-frontend/shared/api/axios";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getMalwareScanMessage } from "@/zapi-frontend/shared/lib/malwareScanError";

function WebsiteIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M6 1.125C5.03582 1.125 4.09329 1.41091 3.2916 1.94659C2.48991 2.48226 1.86507 3.24363 1.49609 4.13442C1.12711 5.02521 1.03057 6.00541 1.21867 6.95107C1.40678 7.89672 1.87108 8.76536 2.55286 9.44715C3.23464 10.1289 4.10328 10.5932 5.04894 10.7813C5.99459 10.9694 6.97479 10.8729 7.86558 10.5039C8.75637 10.1349 9.51775 9.51009 10.0534 8.7084C10.5891 7.90671 10.875 6.96418 10.875 6C10.8735 4.70753 10.3594 3.46842 9.4455 2.5545C8.53159 1.64058 7.29248 1.12649 6 1.125ZM10.1072 5.625H8.24016C8.15625 3.91359 7.49719 2.70328 6.95672 1.9875C7.79591 2.18902 8.55099 2.64814 9.11606 3.30048C9.68114 3.95281 10.0279 4.76565 10.1077 5.625H10.1072ZM4.51078 6.375H7.48922C7.38094 8.32547 6.44719 9.52078 6 9.98438C5.55235 9.52031 4.61906 8.325 4.51078 6.375ZM4.51078 5.625C4.61906 3.67453 5.55281 2.47922 6 2.01562C6.44766 2.48109 7.38094 3.67641 7.48922 5.625H4.51078ZM5.04328 1.9875C4.50281 2.70328 3.84375 3.91359 3.75985 5.625H1.89235C1.97212 4.76565 2.31886 3.95281 2.88394 3.30048C3.44902 2.64814 4.20409 2.18902 5.04328 1.9875ZM1.89235 6.375H3.75985C3.84516 8.08641 4.50281 9.29672 5.04328 10.0125C4.20409 9.81098 3.44902 9.35186 2.88394 8.69952C2.31886 8.04719 1.97212 7.23435 1.89235 6.375ZM6.95485 10.0125C7.49531 9.29672 8.15297 8.08641 8.23828 6.375H10.1058C10.0261 7.23411 9.67966 8.04676 9.11494 8.69906C8.55023 9.35137 7.79557 9.81064 6.95672 10.0125H6.95485Z"
        fill="#52525B"
      />
    </svg>
  );
}

function getValidWebsiteDomain(source: string): string | null {
  const isWebsite = ["https", "www."].some((prefix) => source.startsWith(prefix));

  if (!isWebsite) return null;

  try {
    let urlString = source;
    if (!urlString.startsWith("http://") && !urlString.startsWith("https://")) {
      urlString = "https://" + urlString;
    }

    const url = new URL(urlString);
    let domain = url.hostname;

    if (domain.startsWith("www.")) {
      domain = domain.slice(4);
    }

    return domain;
  } catch (error) {
    return null;
  }
}

export function Source({ source }: { source: string }) {
  const validDomain = getValidWebsiteDomain(source);
  const [isLoading, setIsLoading] = useState(false);

  const { id: projectId } = useParams();
  const { currentOrganizationId } = useOrganizationStore();

  const handleOpenWebsite = () => {
    window.open(source, "_blank", "noopener,noreferrer");
  };

  const handleOpenFile = async () => {
    if (!projectId) return;
    if (!currentOrganizationId) return;

    setIsLoading(true);
    try {
      const response = await $api.get<{ data: { url: string } }>(`/projects/${projectId}/documents/by-name/${source}`, {
        params: { organizationId: currentOrganizationId }
      });
      const url = response.data.data.url;
      const link = document.createElement("a");
      link.href = url;
      link.download = source;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(getMalwareScanMessage(error) ?? "Failed to open file");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger>
        {validDomain && (
          <FigmaTypography
            variant="body-3"
            data-testid={`source-${source}-link`}
            className="text-[12px] leading-[18px] text-innoveraZinc-600 truncate max-w-[170px] min-w-0 cursor-pointer flex items-center gap-1"
            onClick={handleOpenWebsite}
          >
            <WebsiteIcon /> {validDomain}
          </FigmaTypography>
        )}
        {!validDomain && (
          <FigmaTypography
            variant="body-3"
            data-testid={`source-${source}-button`}
            className={`text-[12px] leading-[18px] text-innoveraZinc-600 truncate max-w-[170px] min-w-0 ${
              isLoading ? "cursor-wait opacity-50" : "cursor-pointer"
            }`}
            onClick={handleOpenFile}
          >
            {source}
          </FigmaTypography>
        )}
      </TooltipTrigger>

      <TooltipContent>
        <FigmaTypography variant="body-3" color="zinc600" className="max-w-[300px] wrap-break-word border">
          {source}
        </FigmaTypography>
      </TooltipContent>
    </Tooltip>
  );
}
